import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card/Card';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Switch } from '@/components/ui/Switch/Switch';
import { Modal } from '@/components/ui/Modal/Modal';
import { Select } from '@/components/ui/Select/Select';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { Table, type TableColumn } from '@/components/ui/Table';
import type { BillingPlanDto, BillingPriceDto } from '@/services/BillingService';
import {
  AdminBillingService,
  type AdminPlanCreateInput,
  type AdminPlanUpdateInput,
  type AdminPriceInput,
  type AdminPriceUpdateInput,
  type AdminLimitInput,
  type AdminLimitUpdateInput,
  type AdminFlagInput,
  type AdminFlagUpdateInput,
  type AdminTransactionList,
  type AdminTransactionDto,
  type AdminTransactionQuery,
  type AdminOverrideInput,
} from '@/services/AdminBillingService';
import { useNotificationStore } from '@/stores/notificationStore';

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const sortPlans = (plans: BillingPlanDto[]) =>
  [...plans].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
    return a.name.localeCompare(b.name);
  });

const transactionStatusBadge = (status: AdminTransactionDto['status']) => {
  switch (status) {
    case 'succeeded':
      return <Badge variant="success">Succeeded</Badge>;
    case 'failed':
      return <Badge variant="error">Failed</Badge>;
    case 'refunded':
      return <Badge variant="warning">Refunded</Badge>;
    default:
      return <Badge variant="secondary">Pending</Badge>;
  }
};

type PlanModalState = { mode: 'create' } | { mode: 'edit'; plan: BillingPlanDto };
type PriceModalState =
  | { mode: 'create'; plan: BillingPlanDto }
  | { mode: 'edit'; plan: BillingPlanDto; price: BillingPriceDto };
type PlanLimit = BillingPlanDto['limits'][number];
type LimitModalState =
  | { mode: 'create'; plan: BillingPlanDto }
  | { mode: 'edit'; plan: BillingPlanDto; limit: PlanLimit };
type PlanFlag = BillingPlanDto['flagDetails'][number];
type FlagModalState =
  | { mode: 'create'; plan: BillingPlanDto }
  | { mode: 'edit'; plan: BillingPlanDto; flag: PlanFlag };

interface PlanFormState {
  key: string;
  name: string;
  description: string;
  displayOrder: string;
  isActive: boolean;
  stripeProductId: string;
}

interface PriceFormState {
  interval: 'month' | 'year';
  currency: string;
  unitAmount: string;
  trialDays: string;
  stripePriceId: string;
  paymentLinkUrl: string;
  isActive: boolean;
}

interface LimitFormState {
  limitKey: string;
  limitValue: string;
  isHardLimit: boolean;
}

interface FlagFormState {
  flagKey: string;
  flagValue: string;
}

const statusOptions = [
  { label: 'All statuses', value: '__all__' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Pending', value: 'pending' },
];

export function AdminBillingConsole() {
  const service = React.useMemo(() => new AdminBillingService(), []);
  const [plans, setPlans] = React.useState<BillingPlanDto[]>([]);
  const [loadingPlans, setLoadingPlans] = React.useState(true);
  const [plansError, setPlansError] = React.useState<string | null>(null);

  const [planModal, setPlanModal] = React.useState<PlanModalState | null>(null);
  const [priceModal, setPriceModal] = React.useState<PriceModalState | null>(null);
  const [limitModal, setLimitModal] = React.useState<LimitModalState | null>(null);
  const [flagModal, setFlagModal] = React.useState<FlagModalState | null>(null);

  const [planForm, setPlanForm] = React.useState<PlanFormState>({
    key: '',
    name: '',
    description: '',
    displayOrder: '0',
    isActive: true,
    stripeProductId: '',
  });
  const [planModalError, setPlanModalError] = React.useState<string | null>(null);
  const [planModalSaving, setPlanModalSaving] = React.useState(false);

  const [priceForm, setPriceForm] = React.useState<PriceFormState>({
    interval: 'month',
    currency: 'USD',
    unitAmount: '0',
    trialDays: '0',
    stripePriceId: '',
    paymentLinkUrl: '',
    isActive: true,
  });
  const [priceModalError, setPriceModalError] = React.useState<string | null>(null);
  const [priceModalSaving, setPriceModalSaving] = React.useState(false);

  const [limitForm, setLimitForm] = React.useState<LimitFormState>({
    limitKey: '',
    limitValue: '0',
    isHardLimit: true,
  });
  const [limitModalError, setLimitModalError] = React.useState<string | null>(null);
  const [limitModalSaving, setLimitModalSaving] = React.useState(false);

  const [flagForm, setFlagForm] = React.useState<FlagFormState>({
    flagKey: '',
    flagValue: ''
  });
  const [flagModalError, setFlagModalError] = React.useState<string | null>(null);
  const [flagModalSaving, setFlagModalSaving] = React.useState(false);

  const [transactions, setTransactions] = React.useState<AdminTransactionList | null>(null);
  const [transactionsLoading, setTransactionsLoading] = React.useState(false);
  const [transactionsError, setTransactionsError] = React.useState<string | null>(null);
  const [transactionFilters, setTransactionFilters] = React.useState<{ orgId: string; status: string }>({
    orgId: '',
    status: '__all__',
  });

  const [overrideInput, setOverrideInput] = React.useState<{ orgId: string; planId: string; priceId: string; prorationBehavior: 'create_prorations' | 'none'; cancelAtPeriodEnd: boolean }>({
    orgId: '',
    planId: '',
    priceId: '__keep_current__',
    prorationBehavior: 'create_prorations',
    cancelAtPeriodEnd: false,
  });
  const [overrideSaving, setOverrideSaving] = React.useState(false);
  const [overrideError, setOverrideError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const load = async () => {
      setLoadingPlans(true);
      setPlansError(null);
      try {
        const data = await service.listPlans();
        setPlans(sortPlans(data));
      } catch (err: any) {
        setPlansError(err?.message || 'Failed to load billing plans');
      } finally {
        setLoadingPlans(false);
      }
    };
    load();
  }, [service]);

  React.useEffect(() => {
    if (!planModal) {
      setPlanForm({ key: '', name: '', description: '', displayOrder: '0', isActive: true, stripeProductId: '' });
      setPlanModalError(null);
      return;
    }

    if (planModal.mode === 'create') {
      setPlanForm({ key: '', name: '', description: '', displayOrder: String(plans.length), isActive: true, stripeProductId: '' });
      setPlanModalError(null);
      return;
    }

    const { plan } = planModal;
    setPlanForm({
      key: plan.key,
      name: plan.name,
      description: plan.description ?? '',
      displayOrder: String(plan.displayOrder),
      isActive: plan.isActive,
      stripeProductId: plan.stripeProductId ?? '',
    });
    setPlanModalError(null);
  }, [planModal, plans.length]);

  React.useEffect(() => {
    if (!priceModal) {
      setPriceForm({ interval: 'month', currency: 'USD', unitAmount: '0', trialDays: '0', stripePriceId: '', paymentLinkUrl: '', isActive: true });
      setPriceModalError(null);
      return;
    }

    if (priceModal.mode === 'create') {
      setPriceForm({
        interval: 'month',
        currency: 'USD',
        unitAmount: '0',
        trialDays: '0',
        stripePriceId: '',
        paymentLinkUrl: '',
        isActive: true,
      });
      setPriceModalError(null);
      return;
    }

    const { price } = priceModal;
    setPriceForm({
      interval: price.interval,
      currency: price.currency.toUpperCase(),
      unitAmount: (price.unitAmount / 100).toString(),
      trialDays: String(price.trialDays ?? 0),
      stripePriceId: price.stripePriceId ?? '',
      paymentLinkUrl: price.paymentLinkUrl ?? '',
      isActive: price.isActive,
    });
    setPriceModalError(null);
  }, [priceModal]);

  React.useEffect(() => {
    if (!limitModal) {
      setLimitForm({ limitKey: '', limitValue: '0', isHardLimit: true });
      setLimitModalError(null);
      return;
    }
    if (limitModal.mode === 'create') {
      setLimitForm({ limitKey: '', limitValue: '0', isHardLimit: true });
      setLimitModalError(null);
      return;
    }
    const { limit } = limitModal;
    setLimitForm({ limitKey: limit.key, limitValue: String(limit.value), isHardLimit: limit.isHardLimit });
    setLimitModalError(null);
  }, [limitModal]);

  React.useEffect(() => {
    if (!flagModal) {
      setFlagForm({ flagKey: '', flagValue: '' });
      setFlagModalError(null);
      return;
    }
    if (flagModal.mode === 'create') {
      setFlagForm({ flagKey: '', flagValue: '' });
      setFlagModalError(null);
      return;
    }
    const { flag } = flagModal;
    setFlagForm({ flagKey: flag.key, flagValue: formatFlagValue(flag.value) });
    setFlagModalError(null);
  }, [flagModal]);

  const replacePlan = React.useCallback((plan: BillingPlanDto) => {
    setPlans((prev) => sortPlans([...prev.filter((item) => item.id !== plan.id), plan]));
  }, []);

  const addPlan = React.useCallback((plan: BillingPlanDto) => {
    setPlans((prev) => sortPlans([...prev, plan]));
  }, []);

  const handlePlanSubmit = async () => {
    if (!planModal) return;
    setPlanModalError(null);
    setPlanModalSaving(true);
    try {
      if (planModal.mode === 'create') {
        const payload: AdminPlanCreateInput = {
          key: planForm.key.trim(),
          name: planForm.name.trim(),
          description: planForm.description.trim() ? planForm.description.trim() : null,
          displayOrder: Number(planForm.displayOrder) || 0,
          isActive: planForm.isActive,
          stripeProductId: planForm.stripeProductId.trim() ? planForm.stripeProductId.trim() : null,
        };
        const newPlan = await service.createPlan(payload);
        addPlan(newPlan);
        useNotificationStore.getState().success('Plan created');
      } else {
        const payload: AdminPlanUpdateInput = {
          name: planForm.name.trim(),
          description: planForm.description.trim() ? planForm.description.trim() : null,
          displayOrder: Number(planForm.displayOrder) || 0,
          isActive: planForm.isActive,
          stripeProductId: planForm.stripeProductId.trim() ? planForm.stripeProductId.trim() : null,
        };
        const updatedPlan = await service.updatePlan(planModal.plan.id, payload);
        replacePlan(updatedPlan);
        useNotificationStore.getState().success('Plan updated');
      }
      setPlanModal(null);
    } catch (err: any) {
      setPlanModalError(err?.message || 'Unable to save plan');
    } finally {
      setPlanModalSaving(false);
    }
  };

  const handlePriceSubmit = async () => {
    if (!priceModal) return;
    setPriceModalError(null);
    setPriceModalSaving(true);
    try {
      const basePayload = {
        currency: priceForm.currency.trim().toLowerCase(),
        unitAmount: Math.round(Number(priceForm.unitAmount) * 100) || 0,
        stripePriceId: priceForm.stripePriceId.trim() ? priceForm.stripePriceId.trim() : null,
        paymentLinkUrl: priceForm.paymentLinkUrl.trim() ? priceForm.paymentLinkUrl.trim() : null,
        isActive: priceForm.isActive,
        trialDays: Number(priceForm.trialDays) || 0,
      };

      if (priceModal.mode === 'create') {
        const payload: AdminPriceInput = {
          interval: priceForm.interval,
          ...basePayload,
        };
        const updatedPlan = await service.createPrice(priceModal.plan.id, payload);
        replacePlan(updatedPlan);
        useNotificationStore.getState().success('Price created');
      } else {
        const payload: AdminPriceUpdateInput = basePayload;
        const updatedPlan = await service.updatePrice(priceModal.price.id, payload);
        replacePlan(updatedPlan);
        useNotificationStore.getState().success('Price updated');
      }
      setPriceModal(null);
    } catch (err: any) {
      setPriceModalError(err?.message || 'Unable to save price');
    } finally {
      setPriceModalSaving(false);
    }
  };

  const handleLimitSubmit = async () => {
    if (!limitModal) return;
    setLimitModalError(null);
    setLimitModalSaving(true);
    try {
      const basePayload = {
        limitValue: Number(limitForm.limitValue) || 0,
        isHardLimit: limitForm.isHardLimit,
      };

      if (limitModal.mode === 'create') {
        const payload: AdminLimitInput = {
          limitKey: limitForm.limitKey.trim(),
          ...basePayload,
        };
        const updatedPlan = await service.createLimit(limitModal.plan.id, payload);
        replacePlan(updatedPlan);
        useNotificationStore.getState().success('Limit created');
      } else {
        const payload: AdminLimitUpdateInput = basePayload;
        const updatedPlan = await service.updateLimit(limitModal.limit.id, payload);
        replacePlan(updatedPlan);
        useNotificationStore.getState().success('Limit updated');
      }
      setLimitModal(null);
    } catch (err: any) {
      setLimitModalError(err?.message || 'Unable to save limit');
    } finally {
      setLimitModalSaving(false);
    }
  };

  const handleFlagSubmit = async () => {
    if (!flagModal) return;
    setFlagModalError(null);
    setFlagModalSaving(true);
    try {
      const parsedValue = parseFlagValue(flagForm.flagValue);

      if (flagModal.mode === 'create') {
        const payload: AdminFlagInput = {
          flagKey: flagForm.flagKey.trim(),
          flagValue: parsedValue,
        };
        const updatedPlan = await service.createFlag(flagModal.plan.id, payload);
        replacePlan(updatedPlan);
        useNotificationStore.getState().success('Flag created');
      } else {
        const payload: AdminFlagUpdateInput = {
          flagValue: parsedValue,
        };
        const updatedPlan = await service.updateFlag(flagModal.flag.id, payload);
        replacePlan(updatedPlan);
        useNotificationStore.getState().success('Flag updated');
      }
      setFlagModal(null);
    } catch (err: any) {
      setFlagModalError(err?.message || 'Unable to save flag');
    } finally {
      setFlagModalSaving(false);
    }
  };

  const loadTransactions = async (query?: AdminTransactionQuery) => {
    setTransactionsLoading(true);
    setTransactionsError(null);
    try {
      const data = await service.listTransactions(query);
      setTransactions(data);
    } catch (err: any) {
      setTransactionsError(err?.message || 'Failed to load transactions');
      setTransactions(null);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleTransactionLoad = async () => {
    const query: AdminTransactionQuery = {
      orgId: transactionFilters.orgId.trim() || undefined,
      status: transactionFilters.status && transactionFilters.status !== '__all__' ? (transactionFilters.status as AdminTransactionQuery['status']) : undefined,
      limit: 50,
      offset: 0,
    };
    await loadTransactions(query);
  };

  const handleOverrideSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setOverrideError(null);
    if (!overrideInput.orgId.trim()) {
      setOverrideError('Organization ID is required');
      return;
    }
    if (!overrideInput.planId.trim()) {
      setOverrideError('Select a plan to apply');
      return;
    }
    const payload: AdminOverrideInput = {
      planId: overrideInput.planId,
      priceId: overrideInput.priceId && overrideInput.priceId !== '__keep_current__' ? overrideInput.priceId : null,
      prorationBehavior: overrideInput.prorationBehavior,
      cancelAtPeriodEnd: overrideInput.cancelAtPeriodEnd,
    };

    setOverrideSaving(true);
    try {
      await service.overrideSubscription(overrideInput.orgId.trim(), payload);
      useNotificationStore.getState().success('Subscription override queued');
      setOverrideSaving(false);
    } catch (err: any) {
      setOverrideError(err?.message || 'Failed to override subscription');
      setOverrideSaving(false);
    }
  };

  const priceColumns: TableColumn<AdminTransactionDto>[] = [
    {
      key: 'created_at',
      title: 'Created',
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
      sortable: true,
    },
    {
      key: 'organization_id',
      title: 'Organization',
      render: (value) => <code className="text-xs bg-gray-100 px-2 py-1 rounded">{value}</code>,
    },
    {
      key: 'amount',
      title: 'Amount',
      render: (_value, record) => formatCurrency(record.amount, record.currency.toUpperCase()),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value, record) => transactionStatusBadge(record.status),
    },
    {
      key: 'plan_key',
      title: 'Plan',
      render: (value, record) => value || record.plan_name || '—',
    },
    {
      key: 'price_interval',
      title: 'Interval',
      render: (value) => (value ? value : '—'),
    },
    {
      key: 'paid_at',
      title: 'Paid',
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      key: 'next_payment_attempt_at',
      title: 'Next Attempt',
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
  ];

  const selectedOverridePlan = plans.find((plan) => plan.id === overrideInput.planId);

  const overridePriceOptions = selectedOverridePlan
    ? selectedOverridePlan.prices.map((price) => ({
        value: price.id,
        label: `${price.interval === 'month' ? 'Monthly' : 'Yearly'} — ${formatCurrency(price.unitAmount, price.currency.toUpperCase())} ${price.isActive ? '' : '(inactive)'}`,
      }))
    : [];

  return (
    <PageContainer className="space-y-8">
      <PageHeader
        title="Billing Console"
        description="Manage subscription plans, limits, flags, and review billing transactions."
      />

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Plans</CardTitle>
            <CardDescription>Configure plans, prices, limits, and feature flags.</CardDescription>
          </div>
          <Button onClick={() => setPlanModal({ mode: 'create' })}>New plan</Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {plansError && (
            <div className="rounded-md border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
              {plansError}
            </div>
          )}

          {loadingPlans ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" label="Loading plans" />
            </div>
          ) : (
            <div className="space-y-6">
              {plans.map((plan) => (
                <Card key={plan.id} variant="bordered" padding="lg" className="bg-white">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                        {plan.name}
                        {plan.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                      </h3>
                      <p className="text-sm text-muted-foreground">Key: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{plan.key}</code></p>
                      <p className="text-sm text-muted-foreground">Display order: {plan.displayOrder}</p>
                      {plan.stripeProductId ? (
                        <p className="text-sm text-muted-foreground">Stripe product ID: <code>{plan.stripeProductId}</code></p>
                      ) : (
                        <p className="text-sm text-muted-foreground">Stripe product not linked</p>
                      )}
                      {plan.description && <p className="mt-2 text-sm text-gray-700">{plan.description}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={() => setPlanModal({ mode: 'edit', plan })}>Edit plan</Button>
                      <Button variant="outline" onClick={() => setPriceModal({ mode: 'create', plan })}>Add price</Button>
                      <Button variant="outline" onClick={() => setLimitModal({ mode: 'create', plan })}>Add limit</Button>
                      <Button variant="outline" onClick={() => setFlagModal({ mode: 'create', plan })}>Add flag</Button>
                      <Link to={`/admin/billing/features?planId=${plan.id}`}>
                        <Button variant="outline">Manage features</Button>
                      </Link>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-6 md:grid-cols-3">
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Prices</h4>
                      {plan.prices.length === 0 && <p className="text-sm text-muted-foreground">No prices configured.</p>}
                      {plan.prices.map((price) => (
                        <div key={price.id} className="rounded-lg border border-gray-200 p-3 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm text-gray-900">{price.interval === 'month' ? 'Monthly' : 'Yearly'}</span>
                            {price.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                          </div>
                          <div className="text-sm text-gray-700">{formatCurrency(price.unitAmount, price.currency.toUpperCase())}</div>
                          <div className="text-xs text-muted-foreground">Trial: {price.trialDays} days</div>
                          {price.stripePriceId ? (
                            <div className="text-xs text-muted-foreground">Stripe price: <code>{price.stripePriceId}</code></div>
                          ) : (
                            <div className="text-xs text-muted-foreground">Stripe price not linked</div>
                          )}
                          {price.paymentLinkUrl ? (
                            <div className="text-xs text-muted-foreground truncate">Payment link: <a href={price.paymentLinkUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{price.paymentLinkUrl}</a></div>
                          ) : (
                            <div className="text-xs text-muted-foreground">No payment link</div>
                          )}
                          <div className="pt-2">
                            <Button size="sm" variant="outline" onClick={() => setPriceModal({ mode: 'edit', plan, price })}>
                              Edit price
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Limits</h4>
                      {plan.limits.length === 0 && <p className="text-sm text-muted-foreground">No limits defined.</p>}
                      {plan.limits.map((limit) => (
                        <div key={limit.id} className="rounded-lg border border-gray-200 p-3 space-y-1">
                          <div className="flex items-center justify-between text-sm text-gray-900">
                            <span>{limit.key}</span>
                            {limit.isHardLimit ? <Badge variant="warning">Hard limit</Badge> : <Badge variant="secondary">Soft limit</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">Value: {formatNumber(limit.value)}</div>
                          <div className="pt-2">
                            <Button size="sm" variant="outline" onClick={() => setLimitModal({ mode: 'edit', plan, limit })}>
                              Edit limit
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Flags</h4>
                      {plan.flagDetails.length === 0 && <p className="text-sm text-muted-foreground">No flags defined.</p>}
                      {plan.flagDetails.map((flag) => (
                        <div key={flag.id} className="rounded-lg border border-gray-200 p-3 space-y-1">
                          <div className="text-sm font-medium text-gray-900">{flag.key}</div>
                          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto">
                            {formatFlagValue(flag.value)}
                          </pre>
                          <div className="pt-2">
                            <Button size="sm" variant="outline" onClick={() => setFlagModal({ mode: 'edit', plan, flag })}>
                              Edit flag
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Transactions & Overrides</CardTitle>
            <CardDescription>Inspect recent billing transactions and perform plan overrides for troubleshooting.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => loadTransactions({ limit: 25, offset: 0 })}>Refresh transactions</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <form
            className="grid gap-4 md:grid-cols-4 md:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              handleTransactionLoad();
            }}
          >
            <div className="md:col-span-2">
              <Input
                label="Organization ID"
                placeholder="Filter by organization..."
                value={transactionFilters.orgId}
                onChange={(event) => setTransactionFilters((prev) => ({ ...prev, orgId: event.target.value }))}
              />
            </div>
            <Select
              label="Status"
              value={transactionFilters.status}
              onValueChange={(value) => setTransactionFilters((prev) => ({ ...prev, status: value }))}
              options={statusOptions}
            />
            <Button type="submit">Apply filters</Button>
          </form>

          {transactionsError && (
            <div className="rounded-md border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
              {transactionsError}
            </div>
          )}

          <div className="rounded-lg border border-gray-200">
            <Table
              data={transactions?.items ?? []}
              columns={priceColumns}
              loading={transactionsLoading}
              emptyText={transactionsLoading ? 'Loading transactions...' : 'No transactions found for filters.'}
              searchable={false}
              filters={[]}
            />
          </div>

          <form className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50" onSubmit={handleOverrideSubmit}>
            <h4 className="text-sm font-semibold text-gray-900">Override subscription</h4>
            <p className="text-xs text-muted-foreground">
              Apply a different plan to an organization and optionally switch to a matching price. Use sparingly; this immediately updates the organization record and Stripe subscription.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Organization ID"
                value={overrideInput.orgId}
                onChange={(event) => setOverrideInput((prev) => ({ ...prev, orgId: event.target.value }))}
                required
              />
              <Select
                label="Plan"
                value={overrideInput.planId}
                onValueChange={(value) => {
                  setOverrideInput((prev) => ({ ...prev, planId: value, priceId: '__keep_current__' }));
                }}
                options={plans.map((plan) => ({ value: plan.id, label: `${plan.name}${plan.isActive ? '' : ' (inactive)'}` }))}
                placeholder="Select plan"
              />
              <Select
                label="Price (optional)"
                value={overrideInput.priceId}
                onValueChange={(value) => setOverrideInput((prev) => ({ ...prev, priceId: value }))}
                options={[{ value: '__keep_current__', label: 'Keep current price' }, ...overridePriceOptions]}
              />
              <Select
                label="Proration behavior"
                value={overrideInput.prorationBehavior}
                onValueChange={(value) => setOverrideInput((prev) => ({ ...prev, prorationBehavior: value as 'create_prorations' | 'none' }))}
                options={[
                  { value: 'create_prorations', label: 'Create prorations' },
                  { value: 'none', label: 'No proration' },
                ]}
              />
              <div className="flex items-center gap-3">
                <Switch
                  id="override-cancel"
                  checked={overrideInput.cancelAtPeriodEnd}
                  onChange={(event) => setOverrideInput((prev) => ({ ...prev, cancelAtPeriodEnd: event.target.checked }))}
                />
                <label htmlFor="override-cancel" className="text-sm text-gray-700">
                  Cancel at period end
                </label>
              </div>
            </div>
            {overrideError && <div className="text-sm text-error-600">{overrideError}</div>}
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={overrideSaving}>{overrideSaving ? 'Applying...' : 'Apply override'}</Button>
              <Button type="button" variant="outline" onClick={() => setOverrideInput({ orgId: '', planId: '', priceId: '', prorationBehavior: 'create_prorations', cancelAtPeriodEnd: false })}>
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <PlanModal
        open={Boolean(planModal)}
        onClose={() => setPlanModal(null)}
        form={planForm}
        setForm={setPlanForm}
        error={planModalError}
        saving={planModalSaving}
        mode={planModal?.mode ?? 'create'}
        onSubmit={handlePlanSubmit}
      />

      <PriceModal
        open={Boolean(priceModal)}
        onClose={() => setPriceModal(null)}
        form={priceForm}
        setForm={setPriceForm}
        error={priceModalError}
        saving={priceModalSaving}
        mode={priceModal?.mode ?? 'create'}
        onSubmit={handlePriceSubmit}
        canEditInterval={priceModal?.mode === 'create'}
      />

      <LimitModal
        open={Boolean(limitModal)}
        onClose={() => setLimitModal(null)}
        form={limitForm}
        setForm={setLimitForm}
        error={limitModalError}
        saving={limitModalSaving}
        mode={limitModal?.mode ?? 'create'}
        onSubmit={handleLimitSubmit}
      />

      <FlagModal
        open={Boolean(flagModal)}
        onClose={() => setFlagModal(null)}
        form={flagForm}
        setForm={setFlagForm}
        error={flagModalError}
        saving={flagModalSaving}
        mode={flagModal?.mode ?? 'create'}
        onSubmit={handleFlagSubmit}
      />
    </PageContainer>
  );
}

export function formatFlagValue(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

export function parseFlagValue(input: string): unknown {
  const trimmed = input.trim();
  if (!trimmed.length) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (!Number.isNaN(Number(trimmed))) return Number(trimmed);
    return trimmed;
  }
}

interface PlanModalProps {
  open: boolean;
  onClose: () => void;
  form: PlanFormState;
  setForm: React.Dispatch<React.SetStateAction<PlanFormState>>;
  error: string | null;
  saving: boolean;
  mode: 'create' | 'edit';
  onSubmit: () => void;
}

function PlanModal({ open, onClose, form, setForm, error, saving, mode, onSubmit }: PlanModalProps) {
  return (
    <Modal
      isOpen={open}
      onClose={saving ? () => {} : onClose}
      title={mode === 'create' ? 'Create plan' : 'Edit plan'}
      description={mode === 'create' ? 'Define a new plan and its metadata.' : 'Update plan metadata and activation state.'}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? 'Saving…' : mode === 'create' ? 'Create plan' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {mode === 'create' && (
          <Input
            label="Plan key"
            placeholder="trial"
            value={form.key}
            onChange={(event) => setForm((prev) => ({ ...prev, key: event.target.value }))}
            required
          />
        )}
        <Input
          label="Display name"
          placeholder="Pro"
          value={form.name}
          onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          required
        />
        <Textarea
          label="Description"
          placeholder="Short description for internal reference."
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
        />
        <Input
          label="Display order"
          type="number"
          min={0}
          value={form.displayOrder}
          onChange={(event) => setForm((prev) => ({ ...prev, displayOrder: event.target.value }))}
        />
        <Input
          label="Stripe product ID"
          placeholder="prod_..."
          value={form.stripeProductId}
          onChange={(event) => setForm((prev) => ({ ...prev, stripeProductId: event.target.value }))}
        />
        <div className="flex items-center gap-3">
          <Switch
            id="plan-active"
            checked={form.isActive}
            onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
          />
          <label htmlFor="plan-active" className="text-sm text-gray-700">Plan is active</label>
        </div>
        {error && <div className="text-sm text-error-600">{error}</div>}
      </div>
    </Modal>
  );
}

interface PriceModalProps {
  open: boolean;
  onClose: () => void;
  form: PriceFormState;
  setForm: React.Dispatch<React.SetStateAction<PriceFormState>>;
  error: string | null;
  saving: boolean;
  mode: 'create' | 'edit';
  canEditInterval: boolean;
  onSubmit: () => void;
}

function PriceModal({ open, onClose, form, setForm, error, saving, mode, canEditInterval, onSubmit }: PriceModalProps) {
  return (
    <Modal
      isOpen={open}
      onClose={saving ? () => {} : onClose}
      title={mode === 'create' ? 'Add price' : 'Edit price'}
      description="Configure billing intervals and pricing in major currency units."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving}>{saving ? 'Saving…' : mode === 'create' ? 'Create price' : 'Save changes'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="Interval"
          value={form.interval}
          onValueChange={(value) => setForm((prev) => ({ ...prev, interval: value as 'month' | 'year' }))}
          options={[
            { value: 'month', label: 'Monthly' },
            { value: 'year', label: 'Yearly' },
          ]}
          disabled={!canEditInterval}
        />
        <Input
          label="Currency"
          placeholder="USD"
          value={form.currency}
          onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value }))}
        />
        <Input
          label="Price (major units)"
          type="number"
          min={0}
          step="0.01"
          placeholder="29"
          value={form.unitAmount}
          onChange={(event) => setForm((prev) => ({ ...prev, unitAmount: event.target.value }))}
        />
        <Input
          label="Trial days"
          type="number"
          min={0}
          value={form.trialDays}
          onChange={(event) => setForm((prev) => ({ ...prev, trialDays: event.target.value }))}
        />
        <Input
          label="Stripe price ID"
          placeholder="price_..."
          value={form.stripePriceId}
          onChange={(event) => setForm((prev) => ({ ...prev, stripePriceId: event.target.value }))}
        />
        <Input
          label="Payment link URL"
          placeholder="https://buy.stripe.com/..."
          value={form.paymentLinkUrl}
          onChange={(event) => setForm((prev) => ({ ...prev, paymentLinkUrl: event.target.value }))}
        />
        <div className="flex items-center gap-3">
          <Switch
            id="price-active"
            checked={form.isActive}
            onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
          />
          <label htmlFor="price-active" className="text-sm text-gray-700">Price is active</label>
        </div>
        {error && <div className="text-sm text-error-600">{error}</div>}
      </div>
    </Modal>
  );
}

interface LimitModalProps {
  open: boolean;
  onClose: () => void;
  form: LimitFormState;
  setForm: React.Dispatch<React.SetStateAction<LimitFormState>>;
  error: string | null;
  saving: boolean;
  mode: 'create' | 'edit';
  onSubmit: () => void;
}

function LimitModal({ open, onClose, form, setForm, error, saving, mode, onSubmit }: LimitModalProps) {
  return (
    <Modal
      isOpen={open}
      onClose={saving ? () => {} : onClose}
      title={mode === 'create' ? 'Add limit' : 'Edit limit'}
      description="Hard limits enforce strict caps; soft limits can be overridden elsewhere."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving}>{saving ? 'Saving…' : mode === 'create' ? 'Create limit' : 'Save changes'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Limit key"
          placeholder="llm_calls"
          value={form.limitKey}
          onChange={(event) => setForm((prev) => ({ ...prev, limitKey: event.target.value }))}
          disabled={mode === 'edit'}
        />
        <Input
          label="Limit value"
          type="number"
          min={0}
          value={form.limitValue}
          onChange={(event) => setForm((prev) => ({ ...prev, limitValue: event.target.value }))}
        />
        <div className="flex items-center gap-3">
          <Switch
            id="limit-hard"
            checked={form.isHardLimit}
            onChange={(event) => setForm((prev) => ({ ...prev, isHardLimit: event.target.checked }))}
          />
          <label htmlFor="limit-hard" className="text-sm text-gray-700">Hard limit</label>
        </div>
        {error && <div className="text-sm text-error-600">{error}</div>}
      </div>
    </Modal>
  );
}

interface FlagModalProps {
  open: boolean;
  onClose: () => void;
  form: FlagFormState;
  setForm: React.Dispatch<React.SetStateAction<FlagFormState>>;
  error: string | null;
  saving: boolean;
  mode: 'create' | 'edit';
  onSubmit: () => void;
}

function FlagModal({ open, onClose, form, setForm, error, saving, mode, onSubmit }: FlagModalProps) {
  return (
    <Modal
      isOpen={open}
      onClose={saving ? () => {} : onClose}
      title={mode === 'create' ? 'Add flag' : 'Edit flag'}
      description="Flags allow toggling features or metadata per plan; values accept JSON primitives."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving}>{saving ? 'Saving…' : mode === 'create' ? 'Create flag' : 'Save changes'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Flag key"
          placeholder="priority_support"
          value={form.flagKey}
          onChange={(event) => setForm((prev) => ({ ...prev, flagKey: event.target.value }))}
          disabled={mode === 'edit'}
        />
        <Textarea
          label="Flag value"
          placeholder='true, false, number, or JSON (e.g., {"limits":10})'
          value={form.flagValue}
          onChange={(event) => setForm((prev) => ({ ...prev, flagValue: event.target.value }))}
          rows={6}
        />
        {error && <div className="text-sm text-error-600">{error}</div>}
      </div>
    </Modal>
  );
}
